/* Logicae effects — CanvasUI-inspired, universal, orchestrated.
 * Canvas2D engines behind reduced-motion / GPU / visibility gates.
 * Contract: effects are garnish — every page is complete without them. */
(function () {
    'use strict';

    var reduced = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var eco = document.documentElement.classList.contains('eco');
    var MOBILE = window.innerWidth < 768;
    var DPR = Math.min(window.devicePixelRatio || 1, MOBILE ? 1.5 : 2);

    function gpuOK() {
        try {
            var c = document.createElement('canvas');
            return !!(c.getContext('webgl2') || c.getContext('webgl'));
        } catch (e) { return false; }
    }

    /* ---- one-canvas-per-page rule ---- */
    var canvasOwner = null;

    function makeCanvas(host) {
        if (getComputedStyle(host).position === 'static') {
            host.style.position = 'relative';
        }
        var c = document.createElement('canvas');
        c.className = 'fx-canvas';
        c.setAttribute('aria-hidden', 'true');
        host.appendChild(c);
        return c;
    }

    function sizeCanvas(c, w, h) {
        c.width = Math.max(1, Math.round(w * DPR));
        c.height = Math.max(1, Math.round(h * DPR));
        c.style.width = w + 'px';
        c.style.height = h + 'px';
        var ctx = c.getContext('2d');
        ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
        return ctx;
    }

    /* rAF loop helper; step returns false to stop */
    function loop(step) {
        var raf = null, running = false, last = 0;
        function frame(t) {
            raf = null;
            if (!running) return;
            var dt = last ? Math.min(0.05, (t - last) / 1000) : 0.016;
            last = t;
            if (step(dt) === false) { running = false; return; }
            raf = requestAnimationFrame(frame);
        }
        return {
            wake: function () {
                if (running) return;
                running = true; last = 0;
                raf = requestAnimationFrame(frame);
            },
            sleep: function () {
                running = false;
                if (raf) { cancelAnimationFrame(raf); raf = null; }
            }
        };
    }

    function noop() {}

    /* ================= engine: ink-particles =================
       Text rasterized offscreen becomes the particle target; grains
       assemble from scatter; pointer proximity repels. */
    function inkParticles(host, opts) {
        var canvas = makeCanvas(host);
        var ctx = null, parts = [], W = 0, H = 0, settled = false;
        var color = opts.color || '#16181c';
        var max = opts.max || (MOBILE ? 1600 : 3600);
        var text = opts.text || (host.getAttribute('data-effect-text') || 'LOGICAE');
        var mouse = { x: -9999, y: -9999, at: 0 };

        function build() {
            W = host.clientWidth; H = host.clientHeight;
            if (!W || !H) return false;
            ctx = sizeCanvas(canvas, W, H);
            var off = document.createElement('canvas');
            off.width = W; off.height = H;
            var o = off.getContext('2d', { willReadFrequently: true });
            var size = Math.min(H * 0.62, (W * 0.94) / (text.length * 0.62));
            o.font = '900 ' + size + 'px "Noto Sans KR", sans-serif';
            o.textAlign = 'center';
            o.textBaseline = 'middle';
            o.fillText(text, W / 2, H / 2);
            var data = o.getImageData(0, 0, W, H).data;
            var pts = [];
            for (var y = 0; y < H; y += 2) {
                for (var x = 0; x < W; x += 2) {
                    if (data[(y * W + x) * 4 + 3] > 128) pts.push([x, y]);
                }
            }
            var keep = Math.max(1, Math.ceil(pts.length / max));
            parts = [];
            for (var i = 0; i < pts.length; i += keep) {
                parts.push({
                    hx: pts[i][0], hy: pts[i][1],
                    x: Math.random() * W, y: Math.random() * H,
                    vx: 0, vy: 0
                });
            }
            settled = false;
            return parts.length > 0;
        }

        function step() {
            if (!ctx) return false;
            var energy = 0, mActive = performance.now() - mouse.at < 1500;
            ctx.clearRect(0, 0, W, H);
            ctx.fillStyle = color;
            ctx.globalAlpha = 0.9;
            for (var i = 0; i < parts.length; i++) {
                var p = parts[i];
                p.vx += (p.hx - p.x) * 0.06;
                p.vy += (p.hy - p.y) * 0.06;
                if (mActive) {
                    var dx = p.x - mouse.x, dy = p.y - mouse.y;
                    var d2 = dx * dx + dy * dy;
                    if (d2 < 8100 && d2 > 0.01) {
                        var f = (90 - Math.sqrt(d2)) / 90 * 2.2;
                        var d = Math.sqrt(d2);
                        p.vx += dx / d * f;
                        p.vy += dy / d * f;
                    }
                }
                p.vx *= 0.86; p.vy *= 0.86;
                p.x += p.vx; p.y += p.vy;
                energy += Math.abs(p.vx) + Math.abs(p.vy);
                ctx.fillRect(p.x, p.y, 1.5, 1.5);
            }
            if (!mActive && energy / (parts.length || 1) < 0.02) {
                settled = true;
                return false; /* stop until pointer wakes us */
            }
        }

        var l = loop(step);
        function onMove(e) {
            var r = canvas.getBoundingClientRect();
            mouse.x = e.clientX - r.left;
            mouse.y = e.clientY - r.top;
            mouse.at = performance.now();
            if (settled && mouse.x > -120 && mouse.x < r.width + 120 &&
                mouse.y > -120 && mouse.y < r.height + 120) {
                settled = false;
                l.wake();
            }
        }
        window.addEventListener('pointermove', onMove, { passive: true });

        if (!build()) return null;
        return {
            wake: l.wake,
            sleep: l.sleep,
            resize: function () {
                if (host.clientWidth === W && parts.length) return;
                if (build()) { settled = false; l.wake(); }
            },
            destroy: function () {
                l.sleep();
                window.removeEventListener('pointermove', onMove);
                canvas.remove();
            }
        };
    }

    /* ================= engine: grain-field =================
       Sparse drifting ink grains behind a hero. */
    function grainField(host, opts) {
        var canvas = makeCanvas(host);
        var ctx = null, grains = [], W = 0, H = 0;
        var count = Math.round((opts.count || 140) * (MOBILE ? 0.43 : 1));
        var color = opts.color || '#16181c';
        var alpha = opts.alpha || 0.10;

        function build() {
            W = host.clientWidth; H = host.clientHeight;
            if (!W || !H) return false;
            ctx = sizeCanvas(canvas, W, H);
            grains = [];
            for (var i = 0; i < count; i++) {
                grains.push({
                    x: Math.random() * W, y: Math.random() * H,
                    r: 0.7 + Math.random() * 1.1,
                    a: Math.random() * Math.PI * 2,
                    s: 4 + Math.random() * 10
                });
            }
            return true;
        }

        function step(dt) {
            ctx.clearRect(0, 0, W, H);
            ctx.fillStyle = color;
            for (var i = 0; i < grains.length; i++) {
                var g = grains[i];
                g.a += (Math.random() - 0.5) * 0.3;
                g.x += Math.cos(g.a) * g.s * dt;
                g.y += Math.sin(g.a) * g.s * dt;
                if (g.x < -4) g.x = W + 4; if (g.x > W + 4) g.x = -4;
                if (g.y < -4) g.y = H + 4; if (g.y > H + 4) g.y = -4;
                ctx.globalAlpha = alpha + (g.r - 0.7) * 0.14;
                ctx.beginPath();
                ctx.arc(g.x, g.y, g.r, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        var l = loop(step);
        if (!build()) return null;
        return {
            wake: l.wake, sleep: l.sleep,
            resize: function () { build(); },
            destroy: function () { l.sleep(); canvas.remove(); }
        };
    }

    /* ================= engine: glyph-scramble =================
       DOM text decode effect (no canvas — exempt from one-canvas rule). */
    function glyphScramble(el, opts) {
        var final = el.textContent;
        var glyphs = opts.glyphs || '0123456789▓▒░#%&@$?';
        var dur = opts.duration || 900;
        var t0 = null;
        el.setAttribute('aria-label', final); /* SR reads the real text */
        var l = loop(function () {
            if (t0 === null) t0 = performance.now();
            var k = Math.min(1, (performance.now() - t0) / dur);
            var lock = Math.floor(k * final.length);
            var out = '';
            for (var i = 0; i < final.length; i++) {
                out += (i < lock || final[i] === ' ') ? final[i]
                    : glyphs[Math.floor(Math.random() * glyphs.length)];
            }
            el.textContent = out;
            if (k >= 1) { el.textContent = final; return false; }
        });
        return {
            wake: l.wake, sleep: l.sleep, resize: noop,
            destroy: function () { l.sleep(); el.textContent = final; }
        };
    }

    /* ================= engine: dither-image =================
       Bayer-dithered overlay on the host's <img>, resolving to full
       color. Auto-resolves on mount; setProgress(p) for manual drive. */
    function ditherImage(host, opts) {
        var img = host.querySelector('img');
        if (!img) return null;
        var canvas = makeCanvas(host);
        var ctx = null, off = document.createElement('canvas');
        var W = 0, H = 0, p = 0, ready = false;
        var dur = opts.duration || 0.9;
        var B = [[0, 8, 2, 10], [12, 4, 14, 6], [3, 11, 1, 9], [15, 7, 13, 5]];

        function build() {
            W = host.clientWidth; H = host.clientHeight;
            if (!W || !H || !img.naturalWidth) return false;
            ctx = sizeCanvas(canvas, W, H);
            var sw = Math.max(2, Math.round(W / 5));
            var sh = Math.max(2, Math.round(H / 5));
            off.width = sw; off.height = sh;
            var o = off.getContext('2d', { willReadFrequently: true });
            try {
                o.drawImage(img, 0, 0, sw, sh);
                var d = o.getImageData(0, 0, sw, sh), px = d.data;
            } catch (e) { return false; } /* cross-origin image: skip */
            for (var y = 0; y < sh; y++) {
                for (var x = 0; x < sw; x++) {
                    var i = (y * sw + x) * 4;
                    var g = (px[i] * 0.299 + px[i + 1] * 0.587 + px[i + 2] * 0.114) / 255;
                    var v = g > (B[y % 4][x % 4] + 0.5) / 16 ? 236 : 22;
                    px[i] = px[i + 1] = px[i + 2] = v;
                    px[i + 3] = 255;
                }
            }
            o.putImageData(d, 0, 0);
            ready = true;
            render();
            return true;
        }

        function render() {
            if (!ctx || !ready) return;
            ctx.clearRect(0, 0, W, H);
            ctx.globalAlpha = 1 - p;
            ctx.imageSmoothingEnabled = false;
            ctx.drawImage(off, 0, 0, W, H);
        }

        var l = loop(function (dt) {
            if (!ready) return false; /* wait for image */
            p = Math.min(1, p + dt / dur);
            render();
            if (p >= 1) { canvas.style.display = 'none'; return false; }
        });

        function start() {
            if (build()) l.wake();
        }
        if (img.complete && img.naturalWidth) start();
        else img.addEventListener('load', start, { once: true });

        return {
            wake: l.wake, sleep: l.sleep,
            resize: function () { canvas.style.display = ''; p = 0; start(); },
            destroy: function () { l.sleep(); canvas.remove(); },
            setProgress: function (v) {
                l.sleep();
                p = Math.max(0, Math.min(1, v));
                canvas.style.display = p >= 1 ? 'none' : '';
                render();
            }
        };
    }

    /* ================= orchestrator ================= */
    var ENGINES = {
        'ink-particles': { make: inkParticles, canvas: true },
        'grain-field': { make: grainField, canvas: true },
        'glyph-scramble': { make: glyphScramble, canvas: false },
        'dither-image': { make: ditherImage, canvas: true }
    };

    function mount(el) {
        if (el.__fx) return el.__fx;
        var name = el.getAttribute('data-effect');
        var spec = ENGINES[name];
        if (!spec || reduced) return null;
        if (spec.canvas) {
            if (eco) return null; /* spectacle off on constrained connections */
            if (!gpuOK()) return null;
            if (canvasOwner && canvasOwner !== el) return null; /* one canvas */
        }
        var opts = {};
        try { opts = JSON.parse(el.getAttribute('data-effect-opts') || '{}'); }
        catch (e) { opts = {}; }
        var inst = spec.make(el, opts);
        if (!inst) return null;
        if (spec.canvas) canvasOwner = el;
        el.__fx = inst;

        var visible = true;
        var dirty = false;

        function sync() {
            if (visible && !document.hidden) {
                if (dirty) { dirty = false; inst.resize(); }
                inst.wake();
            } else {
                inst.sleep();
            }
        }

        var io = new IntersectionObserver(function (entries) {
            for (var i = 0; i < entries.length; i++) {
                visible = entries[i].isIntersecting;
            }
            sync();
        }, { rootMargin: '80px' });
        io.observe(el);

        var ro = new ResizeObserver(function () {
            clearTimeout(el.__fxRs);
            el.__fxRs = setTimeout(function () {
                if (visible && !document.hidden) inst.resize();
                else dirty = true;
            }, 200);
        });
        ro.observe(el);

        function onVis() { sync(); }
        document.addEventListener('visibilitychange', onVis);

        var rawDestroy = inst.destroy;
        inst.destroy = function () {
            io.disconnect();
            ro.disconnect();
            document.removeEventListener('visibilitychange', onVis);
            clearTimeout(el.__fxRs);
            rawDestroy();
            if (canvasOwner === el) canvasOwner = null;
            delete el.__fx;
        };

        /* re-rasterize text once webfonts land (ink-particles only) */
        if (name === 'ink-particles' && document.fonts && document.fonts.ready) {
            document.fonts.ready.then(function () {
                if (el.__fx !== inst) return;
                if (visible && !document.hidden) inst.resize();
                else dirty = true;
            });
        }
        return inst;
    }

    window.CanvasFX = { mount: mount, reduced: reduced, gpuOK: gpuOK };

    document.addEventListener('DOMContentLoaded', function () {
        var els = document.querySelectorAll('[data-effect]');
        for (var i = 0; i < els.length; i++) {
            if (!els[i].hasAttribute('data-effect-manual')) mount(els[i]);
        }
    });
})();
